/**
 * Formula engine — the single implementation shared by web, mobile and
 * (via re-export) the backend copy's parity tests.
 *
 * Web re-exports this file from `web/src/utils/formulaCalculation.ts`;
 * mobile imports it from `@iomi/shared`. Add or change rules here only.
 */
import { computeQuoteMaterials } from './utils';
import {
  TRUCK_CAPACITIES,
  type CasegoodsInventoryItem,
  type LogisticsData,
  type OrgInventoryItem,
  type Quote,
  type QuoteCrewMember,
  type QuoteFormula,
  type QuoteResidentialRoom,
  type ResidentialFlatRates,
  type ResourceRate,
  type ResourceSlug,
  type TravelTimeRate,
} from './types';

/**
 * Fold the move's materials (packing items + equipment at the move's rental
 * length) into a computed formula. The subtotal is labor + materials.
 * `materialsCount` is the client-listed quantity (packing items + priced
 * equipment), the "N" in "Materials (×N)" on the client quote.
 */
function applyMaterials(f: QuoteFormula, quote: Quote, orgItems: OrgInventoryItem[]): QuoteFormula {
  const m = computeQuoteMaterials(quote, orgItems);
  return { ...f, materialsCost: m.total, materialsCount: m.listedCount, finalCostEstimate: f.finalCostEstimate + m.total };
}

/** A truck type available for formula recommendations (from org fleet or defaults). */
export interface AvailableTruckType {
  type: string;
  cubicFeet: number;
  weightLimit: number;
}

/**
 * Extract distinct truck types from an org's fleet.
 * Groups trucks by type, uses the capacity/weightLimit from the first truck of each type.
 * Returns undefined if the org has no trucks with type + capacity data (triggers fallback to defaults).
 */
export function extractOrgTruckTypes(
  trucks: Array<{ type?: string; capacity?: number; weightLimit?: number }>,
): AvailableTruckType[] | undefined {
  const byType = new Map<string, { cubicFeet: number; weightLimit: number }>();
  for (const t of trucks) {
    if (!t.type || !t.capacity) continue;
    if (!byType.has(t.type)) {
      byType.set(t.type, { cubicFeet: t.capacity, weightLimit: t.weightLimit ?? 10_000 });
    }
  }
  if (byType.size === 0) return undefined;
  return Array.from(byType.entries())
    .map(([type, caps]) => ({ type, ...caps }))
    .sort((a, b) => a.cubicFeet - b.cubicFeet);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Round a value up to the nearest 0.25 (15 minutes). */
export function ceilQuarter(n: number): number {
  return Math.ceil(n * 4) / 4;
}

/**
 * Origin Optimum Crew Size (or Destination — same formula):
 *   baseline (3 normally, 4 for internal moves)
 *   + 1 if waypoint = Elevator
 *   + 2 if no truck-height loading dock
 *   + Leg1Strides × 0.06
 *   + Leg2Strides × 0.06
 *   + 9 × stories (stairs only)
 *
 * Result is rounded to the nearest integer.
 */
function computeCrewSize(logistics?: LogisticsData, isInternal?: boolean): number {
  const baseline = isInternal ? 4 : 3;
  if (!logistics) return baseline;
  return Math.round(
    baseline
    + (logistics.waypoint === 'Elevator' ? 1 : 0)
    + (!logistics.loadingDock && !isInternal ? 2 : 0)
    + (logistics.strides1 ?? 0) * 0.06
    + (logistics.strides2 ?? 0) * 0.06
    + (logistics.stories ?? 0) * 9
  );
}

/**
 * Shift length hours from move timing. Falls back to 7 (Work week - day)
 * for missing or legacy timing values.
 */
export function getShiftLengthHours(timing?: string): number {
  const map: Record<string, number> = {
    'Work week - day': 7,
    'Work week - evening': 5,
    'Work week - night': 5, // backward compat with older data
    'Weekend': 8,
  };
  return map[timing ?? ''] ?? 7;
}

/**
 * For shuttle jobs, compute the number of trucks and drivers based on
 * transit time (rounded up to the nearest 15 minutes).
 *
 * | Transit (ceil 15 min) | Trucks | Drivers |
 * |-----------------------|--------|---------|
 * | 15 min                | 3      | 2       |
 * | 30 min                | 4      | 3       |
 * | 45 min                | 5      | 3       |
 * | 60 min                | 6      | 4       |
 * | >60 min               | 7      | 4       |
 */
export function getShuttleTrucksAndDrivers(transitSeconds: number): { trucks: number; drivers: number } {
  const transitMinutes = transitSeconds / 60;
  const rounded = Math.ceil(transitMinutes / 15) * 15;

  if (rounded <= 15) return { trucks: 3, drivers: 2 };
  if (rounded <= 30) return { trucks: 4, drivers: 3 };
  if (rounded <= 45) return { trucks: 5, drivers: 3 };
  if (rounded <= 60) return { trucks: 6, drivers: 4 };
  return { trucks: 7, drivers: 4 };
}

/**
 * Build a QuoteCrewMember array from org default resource rates.
 * Used when loading rates into a quote for the first time.
 */
export function buildCrewFromOrgRates(orgRates: ResourceRate[]): QuoteCrewMember[] {
  return orgRates.map(r => ({ slug: r.slug, name: r.name, hourlyRate: r.hourlyRate }));
}

/**
 * Given a computed formula and a stored crew (rates), return the per-resource
 * quantities and rates for display in the breakdown UI.
 *
 * Commercial: finalCrewSize = supervisor + drivers + movers.
 * Residential: finalCrewSize = 1 supervisor (who drives) + movers. No separate drivers.
 */
export function buildCrewFromFormula(
  formula: Pick<QuoteFormula, 'finalCrewSize' | 'method' | 'truckQuantity' | 'driverQuantity'>,
  crew: QuoteCrewMember[],
  quoteType?: 'commercial' | 'residential',
  useBlendedRate?: boolean,
): Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }> {
  const crewMap = new Map(crew.map(c => [c.slug, c]));
  const get = (slug: ResourceSlug) => crewMap.get(slug);

  // If all crew members have stored quantities (set by the formula engine or by a
  // manual breakdown edit), use them directly. This preserves manual edits across navigation.
  if (crew.length > 0 && crew.every(c => c.quantity != null)) {
    return crew
      .map(c => ({ slug: c.slug, name: c.name, quantity: c.quantity!, hourlyRate: c.hourlyRate }))
      .filter(m => m.quantity > 0) as Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }>;
  }

  // Residential: 1 supervisor, rest are movers. Trucks are separate (or blended truck+driver).
  if (quoteType === 'residential') {
    const supervisorQty = 1;
    if (useBlendedRate) {
      const truckDriverQty = formula.truckQuantity;
      const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - truckDriverQty);
      return ([
        { slug: 'supervisor'   as ResourceSlug, name: get('supervisor')?.name ?? 'Supervisor',       quantity: supervisorQty,  hourlyRate: get('supervisor')?.hourlyRate ?? 0 },
        { slug: 'truck_driver' as ResourceSlug, name: get('truck_driver')?.name ?? 'Truck + Driver', quantity: truckDriverQty, hourlyRate: get('truck_driver')?.hourlyRate ?? 0 },
        { slug: 'mover'        as ResourceSlug, name: get('mover')?.name ?? 'Mover',                 quantity: moverQty,       hourlyRate: get('mover')?.hourlyRate ?? 0 },
      ] as Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }>).filter(m => m.quantity > 0);
    }
    const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty);
    return ([
      { slug: 'supervisor' as ResourceSlug, name: get('supervisor')?.name ?? 'Supervisor', quantity: supervisorQty, hourlyRate: get('supervisor')?.hourlyRate ?? 0 },
      { slug: 'mover'      as ResourceSlug, name: get('mover')?.name ?? 'Mover',           quantity: moverQty,      hourlyRate: get('mover')?.hourlyRate ?? 0 },
      { slug: 'truck'      as ResourceSlug, name: get('truck')?.name ?? 'Truck',           quantity: formula.truckQuantity, hourlyRate: get('truck')?.hourlyRate ?? 0 },
    ] as Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }>).filter(m => m.quantity > 0);
  }

  // Commercial fallback: derive quantities from formula fields (first-run or legacy data).
  // Shuttle requires 2 supervisors (one at origin, one at destination).
  // Internal: 1 supervisor, 1 truck, 1 driver (driver counts towards crew size).
  const supervisorQty = formula.method === 'Shuttle' ? 2 : 1;

  if (useBlendedRate) {
    const truckDriverQty = formula.truckQuantity;
    const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - truckDriverQty);
    return ([
      { slug: 'supervisor'   as ResourceSlug, name: get('supervisor')?.name ?? 'Supervisor',       quantity: supervisorQty,  hourlyRate: get('supervisor')?.hourlyRate ?? 0 },
      { slug: 'truck_driver' as ResourceSlug, name: get('truck_driver')?.name ?? 'Truck + Driver', quantity: truckDriverQty, hourlyRate: get('truck_driver')?.hourlyRate ?? 0 },
      { slug: 'mover'        as ResourceSlug, name: get('mover')?.name ?? 'Mover',                 quantity: moverQty,       hourlyRate: get('mover')?.hourlyRate ?? 0 },
    ] as Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }>).filter(m => m.quantity > 0);
  }

  const driverQty = formula.driverQuantity ?? formula.truckQuantity;
  const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - driverQty);

  return ([
    { slug: 'supervisor' as ResourceSlug, name: get('supervisor')?.name ?? 'Supervisor', quantity: supervisorQty, hourlyRate: get('supervisor')?.hourlyRate ?? 0 },
    { slug: 'driver'     as ResourceSlug, name: get('driver')?.name ?? 'Driver',         quantity: driverQty,     hourlyRate: get('driver')?.hourlyRate ?? 0 },
    { slug: 'mover'      as ResourceSlug, name: get('mover')?.name ?? 'Mover',           quantity: moverQty,      hourlyRate: get('mover')?.hourlyRate ?? 0 },
    { slug: 'truck'      as ResourceSlug, name: get('truck')?.name ?? 'Truck',           quantity: formula.truckQuantity, hourlyRate: get('truck')?.hourlyRate ?? 0 },
  ] as Array<{ slug: ResourceSlug; name: string; quantity: number; hourlyRate: number }>).filter(m => m.quantity > 0);
}

// ── Residential formula constants ─────────────────────────────────────────────

/** Gross cubic feet capacity of one truck. */
const TRUCK_CAPACITY_CF = 1700;
/** Fraction of truck volume actually usable (packing efficiency). */
const LOAD_EFFICIENCY = 0.80;
/** Maximum weight per truck in pounds. */
const TRUCK_WEIGHT_LIMIT = 10_000;

/** Per-item handling time in minutes, indexed by move_units tier (1–5). */
const BASE_MINUTES: Record<number, number> = {
  1: 4,   // light, one person, grab and go
  2: 7,   // moderate, one person with effort
  3: 12,  // two-person carry
  4: 20,  // crew or equipment needed
  5: 35,  // specialty item
};

/** Added minutes per item that requires disassembly/reassembly. */
const DISASSEMBLY_MINUTES = 15;
/** Added minutes per bulky item for extra maneuvering. */
const BULKY_MINUTES = 5;

/** Labor-point penalty for items requiring disassembly (≈ doubles handling: disassemble + carry + reassemble). */
const DISASSEMBLY_LABOR_PENALTY = 1.5;
/** Labor-point penalty for bulky items (extra maneuvering/padding without full teardown). */
const BULKY_LABOR_PENALTY = 0.5;

/** Crew size thresholds: totalLaborPoints → minimum crew. */
const CREW_THRESHOLDS: [number, number][] = [
  [80, 2],
  [200, 3],
  [400, 4],
];
const CREW_MAX = 5;

/** Time multiplier per flight of stairs when no elevator. */
const STAIRS_PENALTY_PER_FLIGHT = 0.12;
/** Time multiplier when elevator is available. */
const ELEVATOR_PENALTY = 0.05;
/** Time multiplier for long carry (distant parking). */
const LONG_CARRY_PENALTY = 0.15;

/** Unload speed relative to load (no wrapping, no disassembly at destination). */
const UNLOAD_FACTOR = 0.70;
/** Flat overhead buffer in minutes (walkthrough, paperwork, truck setup, breaks). */
const OVERHEAD_MINUTES = 45;

/**
 * Industry-standard truck types sorted smallest to largest (fallback when org has no fleet).
 */
const DEFAULT_SORTED_TRUCK_TYPES: AvailableTruckType[] = (
  ['cargo_van', 'sprinter_van', '12ft_box', '16ft_box', '20ft_box', '24ft_box', '26ft_box'] as const
).map(t => ({ type: t, ...TRUCK_CAPACITIES[t] }));

/**
 * Compute recommended truck types for a residential move.
 * Uses the org's actual fleet truck types if provided, otherwise falls back
 * to industry-standard defaults.
 *
 * Strategy: start with the largest truck type that can handle the load.
 * Fill as many of that type as possible, then use the next-smallest type
 * for the remainder. Apply load efficiency to volume calculations.
 */
function computeRecommendedTruckTypes(
  totalCubicFeet: number,
  totalWeight: number,
  loadEfficiency: number,
  truckQuantity: number,
  availableTruckTypes?: AvailableTruckType[],
): Array<{ type: string; count: number; capacityPerTruck: number }> {
  if (totalCubicFeet <= 0 && totalWeight <= 0) return [];

  // Work from largest to smallest truck type
  const sortedTypes = availableTruckTypes && availableTruckTypes.length > 0
    ? [...availableTruckTypes].sort((a, b) => a.cubicFeet - b.cubicFeet)
    : DEFAULT_SORTED_TRUCK_TYPES;
  const types = [...sortedTypes].reverse();

  let remainingVolume = totalCubicFeet;
  let remainingWeight = totalWeight;
  let remainingTrucks = truckQuantity;
  const result: Array<{ type: string; count: number; capacityPerTruck: number }> = [];

  for (const truck of types) {
    if (remainingTrucks <= 0) break;
    if (remainingVolume <= 0 && remainingWeight <= 0) break;

    const usableVolume = truck.cubicFeet * loadEfficiency;

    // How many of this truck type do we need based on remaining volume and weight?
    const countByVolume = usableVolume > 0 ? Math.floor(remainingVolume / usableVolume) : 0;
    const countByWeight = truck.weightLimit > 0 ? Math.floor(remainingWeight / truck.weightLimit) : 0;
    const count = Math.min(Math.max(countByVolume, countByWeight), remainingTrucks);

    if (count > 0) {
      result.push({ type: truck.type, count, capacityPerTruck: truck.cubicFeet });
      remainingVolume -= count * usableVolume;
      remainingWeight -= count * truck.weightLimit;
      remainingTrucks -= count;
    }
  }

  // Handle any remainder with the smallest truck type that fits
  if (remainingTrucks > 0 && (remainingVolume > 0 || remainingWeight > 0)) {
    // Find the smallest truck that can handle the remainder
    let bestFit = types[types.length - 1]; // default to smallest
    for (let i = types.length - 1; i >= 0; i--) {
      const t = types[i];
      const usableVol = t.cubicFeet * loadEfficiency;
      if (usableVol >= remainingVolume && t.weightLimit >= remainingWeight) {
        bestFit = t;
      }
    }

    const existing = result.find(r => r.type === bestFit.type);
    if (existing) {
      existing.count += 1;
    } else {
      result.push({ type: bestFit.type, count: 1, capacityPerTruck: bestFit.cubicFeet });
    }
  }

  // Sort result from largest to smallest for display consistency
  return result.sort((a, b) => b.capacityPerTruck - a.capacityPerTruck);
}

/**
 * Compute the formula for a residential quote.
 *
 * Crew Size: labor-point tiers + access bump for 3+ stories without elevator.
 * Labor Hours: item load minutes → parallel crew work → origin/dest access
 *              multipliers → drive time → overhead → round to 15 min.
 */
function computeResidentialFormula(
  quote: Quote,
  travelTimeRate?: TravelTimeRate,
  crew?: QuoteCrewMember[],
  _efficiencyMultiplier?: number,
  useBlendedRate?: boolean,
  fixedTravelTimeHours?: number,
  availableTruckTypes?: AvailableTruckType[],
): QuoteFormula {
  const rooms: QuoteResidentialRoom[] = quote.residentialRooms ?? [];

  // ── Aggregate room item metrics ─────────────────────────────────────────────
  let totalCubicFeet = 0;
  let totalWeight = 0;
  let totalMoveUnits = 0;
  let bulkyItemCount = 0;
  let disassemblyItemCount = 0;
  let totalLaborPoints = 0;
  let totalItemMinutes = 0;

  for (const room of rooms) {
    for (const item of room.items) {
      const qty = item.quantity;
      const mu = item.moveUnits ?? 0;
      totalCubicFeet += (item.cubicFeet ?? 0) * qty;
      totalWeight += (item.weightInPounds ?? 0) * qty;
      totalMoveUnits += mu * qty;
      if (item.bulky) bulkyItemCount += qty;
      if (item.requiredDisassembly) disassemblyItemCount += qty;

      // Labor points per item
      const itemLabor = mu
        + (item.requiredDisassembly ? DISASSEMBLY_LABOR_PENALTY : 0)
        + (item.bulky ? BULKY_LABOR_PENALTY : 0);
      totalLaborPoints += itemLabor * qty;

      // Load minutes per item
      const baseMin = BASE_MINUTES[mu] ?? BASE_MINUTES[1]!;
      const itemLoadMin = baseMin
        + (item.requiredDisassembly ? DISASSEMBLY_MINUTES : 0)
        + (item.bulky ? BULKY_MINUTES : 0);
      totalItemMinutes += itemLoadMin * qty;
    }
  }

  // ── Crew size: tier lookup ────────────────────────────────────────────────
  let crewSizeTier = CREW_MAX;
  for (const [threshold, crewSize] of CREW_THRESHOLDS) {
    if (totalLaborPoints < threshold) {
      crewSizeTier = crewSize;
      break;
    }
  }

  // ── Crew size: access bump ────────────────────────────────────────────────
  const originProp = quote.addresses?.origin?.residentialProperty;
  const destProp = quote.addresses?.destination?.residentialProperty;

  const originStories = originProp?.stories ?? 0;
  const originHasElevator = originProp?.elevator ?? false;
  const originLongCarry = originProp?.longCarry ?? false;

  const destStories = destProp?.stories ?? 0;
  const destHasElevator = destProp?.elevator ?? false;
  const destLongCarry = destProp?.longCarry ?? false;

  const accessBumpApplied =
    (originStories >= 3 && !originHasElevator) ||
    (destStories >= 3 && !destHasElevator);

  const finalCrewSize = crewSizeTier + (accessBumpApplied ? 1 : 0);

  // ── Labor hours: Step 2 — divide by crew (parallel work) ─────────────────
  const baseLoadMinutes = finalCrewSize > 0 ? totalItemMinutes / finalCrewSize : 0;

  // ── Labor hours: Step 3 — origin access multiplier ────────────────────────
  const originFlights = Math.max(originStories - 1, 0);
  let originAccessMultiplier = 1.0;
  if (originHasElevator) {
    originAccessMultiplier += ELEVATOR_PENALTY;
  } else {
    originAccessMultiplier += originFlights * STAIRS_PENALTY_PER_FLIGHT;
  }
  if (originLongCarry) {
    originAccessMultiplier += LONG_CARRY_PENALTY;
  }
  const loadMinutes = baseLoadMinutes * originAccessMultiplier;

  // ── Labor hours: Step 4 — unload time at destination ──────────────────────
  const destFlights = Math.max(destStories - 1, 0);
  let destAccessMultiplier = 1.0;
  if (destHasElevator) {
    destAccessMultiplier += ELEVATOR_PENALTY;
  } else {
    destAccessMultiplier += destFlights * STAIRS_PENALTY_PER_FLIGHT;
  }
  if (destLongCarry) {
    destAccessMultiplier += LONG_CARRY_PENALTY;
  }
  const unloadMinutes = baseLoadMinutes * UNLOAD_FACTOR * destAccessMultiplier;

  // ── Labor hours: Step 5 — drive time ──────────────────────────────────────
  const transitSeconds = quote.addresses?.transit?.durationSeconds ?? 0;
  const driveMinutes = transitSeconds / 60;

  // ── Labor hours: Step 6 — overhead and total ──────────────────────────────
  const totalMinutes = loadMinutes + driveMinutes + unloadMinutes + OVERHEAD_MINUTES;
  const totalHoursRaw = totalMinutes / 60;
  // On-site hours (load + drive + unload + overhead), rounded up to 15 min.
  // Origin→destination driving is already inside these minutes (driveMinutes),
  // so the residential path never bills a separate transit leg on top.
  const onSiteHours = Math.ceil(totalHoursRaw * 4) / 4;

  // ── Truckloads (dual constraint: volume & weight) ────────────────────────
  // Use the largest available truck type for quantity estimation, or fall back to defaults
  const sortedAvailable = availableTruckTypes && availableTruckTypes.length > 0
    ? [...availableTruckTypes].sort((a, b) => b.cubicFeet - a.cubicFeet)
    : undefined;
  const largestCF = sortedAvailable?.[0]?.cubicFeet ?? TRUCK_CAPACITY_CF;
  const largestWt = sortedAvailable?.[0]?.weightLimit ?? TRUCK_WEIGHT_LIMIT;
  const usableCubicFeet = largestCF * LOAD_EFFICIENCY;
  const trucksByVolume = Math.ceil(totalCubicFeet / usableCubicFeet);
  const trucksByWeight = Math.ceil(totalWeight / largestWt);
  const truckQuantity = Math.max(trucksByVolume, trucksByWeight, 1);
  const truckloads = truckQuantity; // residential uses direct truck count
  const driverQuantity = 0; // residential: supervisor drives, no separate drivers

  // ── Recommended truck types ─────────────────────────────────────────────
  const recommendedTruckTypes = computeRecommendedTruckTypes(totalCubicFeet, totalWeight, LOAD_EFFICIENCY, truckQuantity, availableTruckTypes);

  // ── Method ──────────────────────────────────────────────────────────────
  const method: 'Shuttle' | 'Load & Go' = truckQuantity > 4 ? 'Shuttle' : 'Load & Go';

  // ── Travel time (for billing — consistent with commercial formula) ────────
  let travelTimeToJobHours: number;
  if (travelTimeRate === 'portal_to_portal') {
    const originSecs = quote.travelToJob?.originTravelSeconds ?? 0;
    const destSecs = quote.travelToJob?.destinationTravelSeconds ?? 0;
    travelTimeToJobHours = ceilQuarter((originSecs + destSecs) / 3600);
  } else {
    travelTimeToJobHours = fixedTravelTimeHours ?? 1;
  }
  // Transit is inside the on-site hours (see driveMinutes); billing the commercial
  // "transit − 1 h" leg as well would charge the drive twice.
  const finalTransitTimeHours = 0;

  // ── Multi-shift travel time adjustment ──────────────────────────────────────
  // Residential: shifts = ceil(on-site hours / shift length); travel to the job
  // is charged once per shift.
  const resShiftLength = quote.movingDate?.shiftLengthHours ?? getShiftLengthHours(quote.movingDate?.timing);
  const resTravelShifts = resShiftLength > 0
    ? Math.ceil(onSiteHours / resShiftLength)
    : 1;
  travelTimeToJobHours = travelTimeToJobHours * Math.max(1, resTravelShifts);

  // ── Billable hours ─────────────────────────────────────────────────────────
  // Same convention as the commercial and flat-rate paths (and as the Breakdown
  // and Formula Details pages display): on-site duration + travel to the job
  // + transit leg. The stored estimate, the Breakdown and the client quote all
  // read the same total.
  const actualJobDuration = onSiteHours;
  const totalHours = ceilQuarter(actualJobDuration + travelTimeToJobHours + finalTransitTimeHours);

  // ── Crew quantities & cost ────────────────────────────────────────────────
  const crewRateMap = new Map((crew ?? []).map(c => [c.slug, c.hourlyRate]));
  const supervisorQty = 1;

  let totalHourlyRate: number;
  if (useBlendedRate) {
    const truckDriverQty = truckQuantity;
    const moverQty = Math.max(0, finalCrewSize - supervisorQty - truckDriverQty);
    totalHourlyRate =
      supervisorQty   * (crewRateMap.get('supervisor') ?? 0) +
      truckDriverQty  * (crewRateMap.get('truck_driver') ?? 0) +
      moverQty        * (crewRateMap.get('mover') ?? 0);
  } else {
    const moverQty = Math.max(0, finalCrewSize - supervisorQty);
    totalHourlyRate =
      supervisorQty     * (crewRateMap.get('supervisor') ?? 0) +
      moverQty          * (crewRateMap.get('mover') ?? 0) +
      truckQuantity     * (crewRateMap.get('truck') ?? 0);
  }

  const finalCostEstimate = totalHourlyRate * totalHours;

  // ── Bulky/disassembly hours (for display, derived from minutes) ───────────
  const bulkyItemHours = (bulkyItemCount * BULKY_MINUTES) / 60;
  const disassemblyHours = (disassemblyItemCount * DISASSEMBLY_MINUTES) / 60;

  // ── Map to shared QuoteFormula shape ──────────────────────────────────────
  // Some commercial-specific fields get zero/placeholder values.
  const effectiveMultiplier = _efficiencyMultiplier ?? 1;
  const manHours = totalMinutes / 60;

  return {
    // Commercial-shared fields (re-used for display consistency)
    originOptimumCrewSize: finalCrewSize,
    destinationOptimumCrewSize: finalCrewSize,
    combinedOptimumCrewSize: finalCrewSize,
    totalKZ: totalMoveUnits,
    truckloads,
    casegoodsManHours: 0,
    modularBaseManHours: 0,
    modularDisassemblyManHours: 0,
    modularReassemblyManHours: 0,
    modularManHours: 0,
    baseManHours: manHours,
    efficiencyMultiplier: effectiveMultiplier,
    manHours,
    approxCrewSize: finalCrewSize,
    finalCrewSize,
    actualJobDuration,
    method,
    truckQuantity,
    driverQuantity,
    travelTimeToJobHours,
    finalTransitTimeHours,
    totalHours,
    totalHourlyRate,
    finalCostEstimate,

    // Residential-specific intermediate variables
    totalCubicFeet,
    totalWeight,
    totalMoveUnits,
    bulkyItemHours,
    disassemblyHours,
    totalLaborPoints,
    crewSizeTier,
    accessBumpApplied,
    totalItemMinutes,
    baseLoadMinutes,
    originFlights,
    originAccessMultiplier,
    loadMinutes,
    destFlights,
    destAccessMultiplier,
    unloadMinutes,
    driveMinutes,
    overheadMinutes: OVERHEAD_MINUTES,
    totalMinutes,
    bulkyItemCount,
    disassemblyItemCount,
    usableCubicFeet,
    trucksByVolume,
    trucksByWeight,
    recommendedTruckTypes,

    calculatedAt: new Date().toISOString(),
  };
}

// ── Residential flat-rate formula ──────────────────────────────────────────────

export function computeResidentialFlatRateFormula(
  quote: Quote,
  flatRates: ResidentialFlatRates,
  travelTimeRate?: TravelTimeRate,
  crew?: QuoteCrewMember[],
  useBlendedRate?: boolean,
  fixedTravelTimeHours?: number,
  orgItems: OrgInventoryItem[] = [],
): QuoteFormula {
  // ── Match bedroom count to flat rate tier ─────────────────────────────────
  const bedrooms = quote.numberOfBedrooms ?? quote.addresses?.origin?.residentialProperty?.bedrooms ?? 0;
  // Cap at 5 (the "5+" tier)
  const bedroomKey = Math.min(bedrooms, 5);
  const matchedRoom = flatRates.rooms.find(r => r.bedroomCount === bedroomKey && r.enabled);

  const roomPrice = matchedRoom?.flatRate ?? 0;
  const roomLabel = matchedRoom?.label ?? `${bedroomKey} Bedroom${bedroomKey !== 1 ? 's' : ''}`;

  // ── Crew, trucks, hours from the room tier defaults ────────────────────────
  const crewSize = matchedRoom?.crewSize ?? quote.flatRateCrewSize ?? 2;
  const trucks = matchedRoom?.trucks ?? quote.flatRateTrucks ?? 1;
  const jobDurationHours = matchedRoom?.jobDurationHours ?? quote.flatRateJobDurationHours ?? 3;

  // ── Add-on adjustments based on property access ────────────────────────────
  const originProp = quote.addresses?.origin?.residentialProperty;
  const destProp = quote.addresses?.destination?.residentialProperty;

  const addons: Array<{ slug: string; label: string; amount: number }> = [];

  // Flight of stairs: count total flights across origin + destination
  const originFlights = Math.max((originProp?.stories ?? 1) - 1, 0);
  const destFlights = Math.max((destProp?.stories ?? 1) - 1, 0);
  const hasStairsOrigin = originFlights > 0 && !(originProp?.elevator ?? false);
  const hasStairsDest = destFlights > 0 && !(destProp?.elevator ?? false);
  const totalFlights = (hasStairsOrigin ? originFlights : 0) + (hasStairsDest ? destFlights : 0);
  const stairsAddon = flatRates.addons.find(a => a.slug === 'flight_of_stairs');
  if (totalFlights > 0 && stairsAddon && stairsAddon.flatRate > 0) {
    addons.push({
      slug: 'flight_of_stairs',
      label: `${stairsAddon.label} (×${totalFlights})`,
      amount: stairsAddon.flatRate * totalFlights,
    });
  }

  // Elevator access
  const hasElevator = (originProp?.elevator ?? false) || (destProp?.elevator ?? false);
  const elevatorAddon = flatRates.addons.find(a => a.slug === 'elevator_access');
  if (hasElevator && elevatorAddon && elevatorAddon.flatRate > 0) {
    addons.push({
      slug: 'elevator_access',
      label: elevatorAddon.label,
      amount: elevatorAddon.flatRate,
    });
  }

  // Long carry
  const hasLongCarry = (originProp?.longCarry ?? false) || (destProp?.longCarry ?? false);
  const longCarryAddon = flatRates.addons.find(a => a.slug === 'long_carry');
  if (hasLongCarry && longCarryAddon && longCarryAddon.flatRate > 0) {
    addons.push({
      slug: 'long_carry',
      label: longCarryAddon.label,
      amount: longCarryAddon.flatRate,
    });
  }

  const addonTotal = addons.reduce((sum, a) => sum + a.amount, 0);
  const flatRateTotal = roomPrice + addonTotal;

  // ── Travel/transit time ─────────────────────────────────────────────────────
  let travelTimeToJobHours: number;
  if (travelTimeRate === 'portal_to_portal') {
    const originSecs = quote.travelToJob?.originTravelSeconds ?? 0;
    const destSecs = quote.travelToJob?.destinationTravelSeconds ?? 0;
    travelTimeToJobHours = ceilQuarter((originSecs + destSecs) / 3600);
  } else {
    travelTimeToJobHours = fixedTravelTimeHours ?? 1;
  }

  const transitSeconds = quote.addresses?.transit?.durationSeconds ?? 0;
  const transitHours = transitSeconds / 3600;
  const finalTransitTimeHours = ceilQuarter(transitHours < 1 ? 0 : transitHours - 1);

  // ── Multi-shift travel time adjustment ──────────────────────────────────────
  // Residential: shifts = ceil((job duration + transit origin→dest) / shift length)
  const flatShiftLength = quote.movingDate?.shiftLengthHours ?? getShiftLengthHours(quote.movingDate?.timing);
  const flatTravelShifts = flatShiftLength > 0
    ? Math.ceil((jobDurationHours + transitHours) / flatShiftLength)
    : 1;
  travelTimeToJobHours = travelTimeToJobHours * Math.max(1, flatTravelShifts);

  // Total hours = configured job duration + transit + travel (adjusted for multi-shift)
  const totalHours = ceilQuarter(jobDurationHours + finalTransitTimeHours + travelTimeToJobHours);

  // ── Crew hourly rate (for scheduling & display — not used for pricing) ──────
  const crewRateMap = new Map((crew ?? []).map(c => [c.slug, c.hourlyRate]));
  const supervisorQty = 1;

  let totalHourlyRate: number;
  if (useBlendedRate) {
    const truckDriverQty = trucks;
    const moverQty = Math.max(0, crewSize - supervisorQty - truckDriverQty);
    totalHourlyRate =
      supervisorQty * (crewRateMap.get('supervisor') ?? 0) +
      truckDriverQty * (crewRateMap.get('truck_driver') ?? 0) +
      moverQty * (crewRateMap.get('mover') ?? 0);
  } else {
    const moverQty = Math.max(0, crewSize - supervisorQty);
    totalHourlyRate =
      supervisorQty * (crewRateMap.get('supervisor') ?? 0) +
      moverQty * (crewRateMap.get('mover') ?? 0) +
      trucks * (crewRateMap.get('truck') ?? 0);
  }

  return applyMaterials({
    originOptimumCrewSize: crewSize,
    destinationOptimumCrewSize: crewSize,
    combinedOptimumCrewSize: crewSize,
    totalKZ: 0,
    truckloads: trucks,
    casegoodsManHours: 0,
    modularBaseManHours: 0,
    modularDisassemblyManHours: 0,
    modularReassemblyManHours: 0,
    modularManHours: 0,
    baseManHours: 0,
    efficiencyMultiplier: 1,
    manHours: 0,
    approxCrewSize: crewSize,
    finalCrewSize: crewSize,
    actualJobDuration: jobDurationHours,
    method: 'Load & Go',
    truckQuantity: trucks,
    driverQuantity: 0,
    travelTimeToJobHours,
    finalTransitTimeHours,
    totalHours,
    totalHourlyRate,
    finalCostEstimate: flatRateTotal,

    // Flat-rate-specific fields
    isFlatRate: true,
    flatRateRoomPrice: roomPrice,
    flatRateRoomLabel: roomLabel,
    flatRateBedroomCount: bedroomKey,
    flatRateAddons: addons,
    flatRateTotal,

    calculatedAt: new Date().toISOString(),
  }, quote, orgItems);
}

// ── Main export ───────────────────────────────────────────────────────────────

export function computeFormula(
  quote: Quote,
  orgItems: OrgInventoryItem[],
  travelTimeRate?: TravelTimeRate,
  crew?: QuoteCrewMember[],
  efficiencyMultiplier?: number,
  useBlendedRate?: boolean,
  fixedTravelTimeHours?: number,
  availableTruckTypes?: AvailableTruckType[],
): QuoteFormula {
  // Residential quotes use a different formula path
  if (quote.type === 'residential') {
    return applyMaterials(computeResidentialFormula(quote, travelTimeRate, crew, efficiencyMultiplier, useBlendedRate, fixedTravelTimeHours, availableTruckTypes), quote, orgItems);
  }

  const inventory: CasegoodsInventoryItem[] = quote.casegoodsInventory ?? [];
  const itemMap = new Map(orgItems.map(i => [i.id, i]));

  // ── Crew sizes ──────────────────────────────────────────────────────────────
  const isInternal = !!quote.addresses?.internalMove;
  const originOptimumCrewSize = computeCrewSize(quote.originLogistics, isInternal);
  // For internal moves, destination crew size is treated as 0 (no transit leg)
  const destinationOptimumCrewSize = isInternal
    ? 0
    : computeCrewSize(quote.destinationLogistics);
  const combinedOptimumCrewSize = originOptimumCrewSize + destinationOptimumCrewSize;

  // ── Inventory totals — split by type ────────────────────────────────────────
  let casegoodsKZ = 0;
  let modularKZ = 0;
  let additionalManHoursTotal = 0;
  for (const inv of inventory) {
    const item = itemMap.get(inv.itemId);
    if (!item) continue;
    const kz = (item.kz ?? 0) * inv.quantity;
    if (item.type === 'Casegoods') {
      casegoodsKZ += kz;
    } else {
      // 'Modular Component' | 'Modular Equipment'
      modularKZ += kz;
    }
    if (item.isAccessorial) {
      additionalManHoursTotal += ((item.accessorialOriginManHours ?? 0) + (item.accessorialDestinationManHours ?? 0)) * inv.quantity;
    } else {
      additionalManHoursTotal += (item.additionalManHours ?? 0) * inv.quantity;
    }
  }
  const totalKZ = casegoodsKZ + modularKZ;

  // ── Formula chain ───────────────────────────────────────────────────────────
  const truckloads = totalKZ / 700;

  const timing = quote.movingDate?.timing;
  const shiftLengthHours = quote.movingDate?.shiftLengthHours ?? getShiftLengthHours(timing);

  const effectiveMultiplier = efficiencyMultiplier ?? 1;

  // Casegoods man-hours: KZ-based portion for Casegoods items only
  const casegoodsManHours = (casegoodsKZ / 700) * combinedOptimumCrewSize;

  // Modular man-hours: KZ-based portion for Modular items + disassembly/reassembly adjustments
  const modularBaseManHours = (modularKZ / 700) * combinedOptimumCrewSize;
  const moverDisassembling = quote.modularWorkstations?.moverDisassembling ?? false;
  const moverReassembling  = quote.modularWorkstations?.moverReassembling  ?? false;
  const modularDisassemblyManHours = moverDisassembling ? modularBaseManHours * 0.05 : 0;
  const modularReassemblyManHours = moverReassembling ? modularBaseManHours * 0.3 : 0;
  const modularManHours = modularBaseManHours + modularDisassemblyManHours + modularReassemblyManHours;

  // Building protection man-hours: truckloads beyond 6 require additional time
  const buildingProtectionManHours = Math.max(0, Math.ceil(truckloads) - 6);

  // Base man-hours = casegoods + modular + additional man-hours + building protection
  const baseManHours = casegoodsManHours + modularManHours + additionalManHoursTotal + buildingProtectionManHours;

  // Final man-hours = base × efficiency multiplier
  const manHours = baseManHours * effectiveMultiplier;

  // ── Method & truck/driver quantity ─────────────────────────────────────────
  const method: 'Shuttle' | 'Load & Go' | 'Internal' = isInternal
    ? 'Internal'
    : truckloads > 4 ? 'Shuttle' : 'Load & Go';
  const transitSeconds = quote.addresses?.transit?.durationSeconds ?? 0;

  let truckQuantity: number;
  let driverQuantity: number;
  if (isInternal) {
    // Internal moves: 1 truck (to transport crew, equipment, and materials)
    // and 1 driver who counts towards the crew size
    truckQuantity = 1;
    driverQuantity = 1;
  } else if (method === 'Shuttle') {
    truckQuantity = getShuttleTrucksAndDrivers(transitSeconds).trucks;
    driverQuantity = truckQuantity;
  } else {
    // Load & Go: the final truck count depends on how many shifts the job
    // spans, which requires actualJobDuration. Seed with a placeholder here
    // and finalize below after the crew/duration pipeline runs.
    truckQuantity = 1;
    driverQuantity = 1;
  }

  // ── Approximate crew size with multi-shift fallback ────────────────────────
  // Max crew depends on method:
  //   Load & Go / Internal: min(originOptimum, destinationOptimum) — the bottleneck site
  //     (Internal follows Load & Go rules: one supervisor, max crew = smallest optimum)
  //   Shuttle:   combinedOptimumCrewSize + transit-table drivers
  //             (transit-table drivers determine the max ceiling, not the actual
  //              crew composition where drivers = truckQuantity)
  const shuttleTableDrivers = method === 'Shuttle'
    ? getShuttleTrucksAndDrivers(transitSeconds).drivers
    : 0;
  const maxAllowedCrewSize = method === 'Shuttle'
    ? combinedOptimumCrewSize + shuttleTableDrivers
    : (isInternal
        ? originOptimumCrewSize
        : (destinationOptimumCrewSize > 0
            ? Math.min(originOptimumCrewSize, destinationOptimumCrewSize)
            : originOptimumCrewSize));

  // Start at 1 shift; if approx crew exceeds max, add shifts until it fits
  let shifts = 1;
  let rawApproxCrewSize = shiftLengthHours > 0 ? manHours / (shifts * shiftLengthHours) : 0;
  while (rawApproxCrewSize > maxAllowedCrewSize && shiftLengthHours > 0) {
    shifts++;
    rawApproxCrewSize = manHours / (shifts * shiftLengthHours);
  }
  const approxCrewSize = rawApproxCrewSize;
  let finalCrewSize = Math.ceil(approxCrewSize);

  // High-volume commercial override: when a job spans 7 or more truckloads, default
  // the actual crew to the combined optimum crew size (origin optimum + destination
  // optimum) plus the shuttle drivers. At this scale, staffing both sites at full
  // optimum plus the transit-table drivers is the most efficient use of resources, so
  // we lock the actual crew to that combined ceiling. (7+ truckloads is always a
  // Shuttle move, so shuttleTableDrivers is the transit-table driver count; it is 0
  // only for the Internal-method edge case.)
  if (truckloads >= 7) {
    finalCrewSize = combinedOptimumCrewSize + shuttleTableDrivers;
  }

  const actualJobDuration = ceilQuarter(finalCrewSize > 0 ? manHours / finalCrewSize : 0);

  // ── Load & Go truck count: truckloads spread across job shifts ────────────
  // shiftCount = roundup(actualJobDuration / shiftLength)
  // truckQuantity = max(1, roundup(truckloads / shiftCount))
  if (method === 'Load & Go') {
    const shiftCount = shiftLengthHours > 0
      ? Math.max(1, Math.ceil(actualJobDuration / shiftLengthHours))
      : 1;
    truckQuantity = Math.max(1, Math.ceil(truckloads / shiftCount));
    driverQuantity = truckQuantity;
  }

  // ── Travel & transit times ──────────────────────────────────────────────────
  // Travel time to job: org's fixed hours for one_hour_standard; sum of warehouse travel for portal_to_portal
  let travelTimeToJobHours: number;
  if (travelTimeRate === 'portal_to_portal') {
    const originSecs = quote.travelToJob?.originTravelSeconds ?? 0;
    const destSecs = quote.travelToJob?.destinationTravelSeconds ?? 0;
    travelTimeToJobHours = ceilQuarter((originSecs + destSecs) / 3600);
  } else {
    travelTimeToJobHours = fixedTravelTimeHours ?? 1;
  }

  // Final transit time: transit < 1 hr → 0, otherwise transit − 1 hr
  const transitHours = transitSeconds / 3600;
  const finalTransitTimeHours = ceilQuarter(transitHours < 1 ? 0 : transitHours - 1);

  // ── Multi-shift travel time adjustment ────────────────────────────────────
  // Commercial: shifts = ceil(actual job duration / shift length)
  // Travel time is charged once per shift (crew travels to/from job each shift)
  const travelShifts = shiftLengthHours > 0 ? Math.ceil(actualJobDuration / shiftLengthHours) : 1;
  const adjustedTravelTimeToJobHours = travelTimeToJobHours * Math.max(1, travelShifts);

  // Total billable hours (rounded up to nearest 15 min after summing)
  const totalHours = ceilQuarter(actualJobDuration + adjustedTravelTimeToJobHours + finalTransitTimeHours);

  // ── Crew quantities & cost ──────────────────────────────────────────────────
  const crewRateMap = new Map((crew ?? []).map(c => [c.slug, c.hourlyRate]));
  // Shuttle requires 2 supervisors (one at origin, one at destination)
  const supervisorQty = method === 'Shuttle' ? 2 : 1;

  let totalHourlyRate: number;
  if (useBlendedRate) {
    const truckDriverQty = truckQuantity;
    const moverQty = Math.max(0, finalCrewSize - supervisorQty - truckDriverQty);
    totalHourlyRate =
      supervisorQty   * (crewRateMap.get('supervisor') ?? 0) +
      truckDriverQty  * (crewRateMap.get('truck_driver') ?? 0) +
      moverQty        * (crewRateMap.get('mover') ?? 0);
  } else {
    const moverQty = Math.max(0, finalCrewSize - supervisorQty - driverQuantity);
    totalHourlyRate =
      supervisorQty     * (crewRateMap.get('supervisor') ?? 0) +
      driverQuantity    * (crewRateMap.get('driver') ?? 0) +
      moverQty          * (crewRateMap.get('mover') ?? 0) +
      truckQuantity     * (crewRateMap.get('truck') ?? 0);
  }

  const finalCostEstimate = totalHourlyRate * totalHours;

  return applyMaterials({
    originOptimumCrewSize,
    destinationOptimumCrewSize,
    combinedOptimumCrewSize,
    totalKZ,
    truckloads,
    casegoodsManHours,
    modularBaseManHours,
    modularDisassemblyManHours,
    modularReassemblyManHours,
    modularManHours,
    buildingProtectionManHours,
    baseManHours,
    efficiencyMultiplier: effectiveMultiplier,
    manHours,
    approxCrewSize,
    finalCrewSize,
    actualJobDuration,
    method,
    truckQuantity,
    driverQuantity,
    travelTimeToJobHours: adjustedTravelTimeToJobHours,
    finalTransitTimeHours,
    totalHours,
    totalHourlyRate,
    finalCostEstimate,
    calculatedAt: new Date().toISOString(),
  }, quote, orgItems);
}
